import { useParams, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
	ArrowLeft,
	Pencil,
	TestTube2,
	History,
	RotateCcw,
} from "lucide-react";
import {
	useMappingProfile,
	useMappingProfileVersions,
	useMappingProfileAudit,
	useRollbackVersion,
} from "@/hooks/useMappingProfiles";
import { format } from "date-fns";

const templateTypeLabels: Record<string, string> = {
	invoice: "Invoice",
	invoice_note: "CN/DN/RN",
	self_billed_invoice: "Self-Billed Invoice",
	self_billed_invoice_note: "Self-Billed CN/DN/RN",
};

const MappingProfileDetailPage = () => {
	const { id } = useParams<{ id: string }>();
	const [, navigate] = useLocation();

	const { profile, columns, isLoading } = useMappingProfile(id ?? "");
	const { versions } = useMappingProfileVersions(id ?? "");
	const { auditLogs } = useMappingProfileAudit(id ?? "");
	const { rollback, isRollingBack } = useRollbackVersion();

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="text-muted-foreground">Loading...</div>
			</div>
		);
	}

	if (!profile) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="text-muted-foreground">Profile not found.</div>
			</div>
		);
	}

	const applicableTypeCodes = Array.isArray(profile.applicableTypeCodes)
		? profile.applicableTypeCodes
		: [];

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-4">
					<Button
						variant="ghost"
						size="icon"
						onClick={() => navigate("/mapping-profiles")}
					>
						<ArrowLeft className="h-4 w-4" />
					</Button>
					<div>
						<h1 className="text-2xl font-bold">{profile.name}</h1>
						<p className="text-muted-foreground">
							{profile.description || "No description"}
						</p>
					</div>
				</div>
				<div className="flex items-center gap-2">
					<Button
						variant="outline"
						onClick={() =>
							navigate(`/mapping-profiles/${id}/test`)
						}
					>
						<TestTube2 className="mr-2 h-4 w-4" />
						Test
					</Button>
					<Button
						onClick={() =>
							navigate(`/mapping-profiles/${id}/edit`)
						}
					>
						<Pencil className="mr-2 h-4 w-4" />
						Edit Columns
					</Button>
				</div>
			</div>

			{/* Profile Overview */}
			<Card>
				<CardContent className="pt-6">
					<div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
						<div>
							<span className="text-muted-foreground block">
								Vendor
							</span>
							<span className="font-medium">
								{profile.vendorName || "-"}
							</span>
						</div>
						<div>
							<span className="text-muted-foreground block">
								Template
							</span>
							<span className="font-medium">
								{templateTypeLabels[
									profile.targetTemplateType
								] || profile.targetTemplateType}
							</span>
						</div>
						<div>
							<span className="text-muted-foreground block">
								Type Codes
							</span>
							<div className="flex flex-wrap gap-1 mt-0.5">
								{applicableTypeCodes.map((tc: string) => (
									<Badge key={tc} variant="outline">
										{tc}
									</Badge>
								))}
							</div>
						</div>
						<div>
							<span className="text-muted-foreground block">
								Status
							</span>
							{profile.isActive ? (
								<Badge className="bg-green-100 text-green-800">
									Active
								</Badge>
							) : (
								<Badge variant="secondary">Inactive</Badge>
							)}
						</div>
						<div>
							<span className="text-muted-foreground block">
								Source
							</span>
							<span className="font-medium">
								{profile.aiGenerated ? "AI-Generated" : "Manual"}
								{profile.overallConfidence != null &&
									` (${profile.overallConfidence}% confidence)`}
							</span>
						</div>
						<div>
							<span className="text-muted-foreground block">
								Columns Mapped
							</span>
							<span className="font-medium">
								{columns.length}
							</span>
						</div>
						<div>
							<span className="text-muted-foreground block">
								Data Start Row
							</span>
							<span className="font-medium">
								{profile.dataStartRow}
							</span>
						</div>
						<div>
							<span className="text-muted-foreground block">
								Created
							</span>
							<span className="font-medium">
								{format(
									new Date(profile.createdAt),
									"dd MMM yyyy",
								)}
							</span>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Tabs: Columns, Versions, Audit */}
			<Tabs defaultValue="columns">
				<TabsList>
					<TabsTrigger value="columns">
						Columns ({columns.length})
					</TabsTrigger>
					<TabsTrigger value="versions">
						<History className="mr-1 h-3 w-3" />
						Versions ({versions.length})
					</TabsTrigger>
					<TabsTrigger value="audit">
						Audit ({auditLogs.length})
					</TabsTrigger>
				</TabsList>

				{/* Columns Tab */}
				<TabsContent value="columns">
					<Card>
						<CardContent className="pt-4">
							<div className="overflow-x-auto">
								<table className="w-full text-sm">
									<thead>
										<tr className="border-b text-left">
											<th className="py-2 px-2">
												Target
											</th>
											<th className="py-2 px-2">
												Source
											</th>
											<th className="py-2 px-2">
												Transform
											</th>
											<th className="py-2 px-2">
												Required
											</th>
										</tr>
									</thead>
									<tbody>
										{columns.map((col) => (
											<tr
												key={col.id}
												className="border-b"
											>
												<td className="py-2 px-2">
													<span className="text-muted-foreground text-xs">
														{col.targetSheet}.
													</span>
													{col.targetColumn}
												</td>
												<td className="py-2 px-2">
													<span className="text-muted-foreground text-xs">
														{col.sourceSheet}.
													</span>
													{col.sourceColumn}
												</td>
												<td className="py-2 px-2">
													{col.transformType !==
													"none" ? (
														<Badge variant="secondary">
															{col.transformType}
														</Badge>
													) : (
														"-"
													)}
												</td>
												<td className="py-2 px-2">
													{col.isRequired
														? "Yes"
														: "-"}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</CardContent>
					</Card>
				</TabsContent>

				{/* Versions Tab */}
				<TabsContent value="versions">
					<Card>
						<CardContent className="pt-4">
							<div className="space-y-3">
								{versions.map((v) => (
									<div
										key={v.id}
										className="flex items-center justify-between border-b pb-3"
									>
										<div>
											<span className="font-medium">
												Version {v.versionNumber}
											</span>
											{v.versionLabel && (
												<span className="text-muted-foreground ml-2">
													{v.versionLabel}
												</span>
											)}
											{v.changeSummary && (
												<p className="text-xs text-muted-foreground">
													{v.changeSummary}
												</p>
											)}
											<p className="text-xs text-muted-foreground">
												{format(
													new Date(v.createdAt),
													"dd MMM yyyy HH:mm",
												)}
											</p>
										</div>
										{profile.currentVersionId !==
											v.id && (
											<Button
												variant="outline"
												size="sm"
												disabled={isRollingBack}
												onClick={() =>
													rollback({
														id: id!,
														versionNumber:
															v.versionNumber,
													})
												}
											>
												<RotateCcw className="mr-1 h-3 w-3" />
												Rollback
											</Button>
										)}
									</div>
								))}
							</div>
						</CardContent>
					</Card>
				</TabsContent>

				{/* Audit Tab */}
				<TabsContent value="audit">
					<Card>
						<CardContent className="pt-4">
							<div className="space-y-2">
								{auditLogs.map((log) => (
									<div
										key={log.id}
										className="flex items-center gap-3 text-sm border-b pb-2"
									>
										<Badge variant="outline">
											{log.action}
										</Badge>
										<span className="text-muted-foreground">
											{format(
												new Date(log.loggedAt),
												"dd MMM yyyy HH:mm",
											)}
										</span>
										<span className="text-xs text-muted-foreground">
											{log.actorEmail || log.actorId}
										</span>
									</div>
								))}
								{auditLogs.length === 0 && (
									<p className="text-muted-foreground text-sm">
										No audit entries yet.
									</p>
								)}
							</div>
						</CardContent>
					</Card>
				</TabsContent>
			</Tabs>
		</div>
	);
};

export default MappingProfileDetailPage;
